import {
  InboxPanelContext,
  InboxPanelState,
} from "@/contexts/InboxPanelContext";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";
import { localStorageManager } from "@/services/localStorage";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useInboxPanelAnalytics } from "@/hooks/useInboxPanelAnalytics";
import { HEADER_HEIGHT_PX } from "@/lib/layout";

// Responsive width configuration
const PANEL_WIDTHS = {
  desktop: 500, // >= 1024px
  tablet: 400, // 768px - 1023px
  mobile: window.innerWidth || 375, // < 768px (full width)
} as const;

// Breakpoints matching Tailwind defaults
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
} as const;

const getResponsivePanelWidth = (): number => {
  if (typeof window === "undefined") return PANEL_WIDTHS.desktop;

  const width = window.innerWidth;

  if (width < BREAKPOINTS.mobile) {
    return width; // Full width on mobile
  } else if (width < BREAKPOINTS.tablet) {
    return PANEL_WIDTHS.tablet;
  } else {
    return PANEL_WIDTHS.desktop;
  }
};

export const InboxPanelProvider = ({ children }: PropsWithChildren) => {
  // Initialize analytics hook
  const { trackPanelToggle } = useInboxPanelAnalytics();

  // Initialize panel state from localStorage with fallback
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    const stored = localStorageManager.getItem(
      LOCAL_STORAGE_BASE_KEYS.INBOX_PANEL_OPEN
    );
    return stored === "true" || false;
  });

  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const [panelWidth, setPanelWidth] = useState<number>(getResponsivePanelWidth);
  const [headerHeight, setHeaderHeightState] =
    useState<number>(HEADER_HEIGHT_PX);

  const [rememberedTab, setRememberedTab] = useState<string | null>(() =>
    localStorageManager.getItem(LOCAL_STORAGE_BASE_KEYS.NOTIFICATION_SHEET_TAB)
  );
  // In-memory only, and deliberately never cleared on a shop change: switching
  // shops (src/components/Page/Sidebar.tsx) is a full `window.location.href`
  // page load, which tears down React and discards this value on its own. If
  // a stale `?ro=` somehow survived that reload, `useCard` simply fails to
  // find the id and the panel falls back to `RoDetailsEmptyState` -- the same
  // behavior as `dev` today.
  const [rememberedCardId, setRememberedCardId] = useState<string | null>(null);

  const [hasSearchResults, setHasSearchResultsState] = useState<boolean>(false);

  // Handle responsive width changes
  useEffect(() => {
    const handleResize = () => {
      const newWidth = getResponsivePanelWidth();
      setPanelWidth(newWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate header height dynamically
  useEffect(() => {
    const calculateHeaderHeight = () => {
      // Look for the main header element by common selectors
      const headerSelectors = [
        '[data-testid="dashboard-header"]',
        ".dashboard-header",
        "header",
        '[role="banner"]',
      ];

      let headerElement = null;
      for (const selector of headerSelectors) {
        headerElement = document.querySelector(selector);
        if (headerElement) break;
      }

      if (headerElement) {
        const height = headerElement.getBoundingClientRect().height;
        setHeaderHeightState(height);
      } else {
        setHeaderHeightState(HEADER_HEIGHT_PX);
      }
    };

    // Calculate on mount and window resize
    calculateHeaderHeight();
    window.addEventListener("resize", calculateHeaderHeight);

    return () => window.removeEventListener("resize", calculateHeaderHeight);
  }, []);

  // Persist panel state to localStorage
  useEffect(() => {
    localStorageManager.setItem(
      LOCAL_STORAGE_BASE_KEYS.INBOX_PANEL_OPEN,
      isOpen.toString()
    );
  }, [isOpen]);

  const togglePanel = useCallback(() => {
    const newState = !isOpen;
    const action = newState ? "opened" : "closed";

    setIsTransitioning(true);
    setIsOpen(newState);

    // Track panel toggle analytics
    try {
      trackPanelToggle(action, "header_button", panelWidth);
    } catch (error) {
      console.error("Failed to track panel toggle:", error);
    }

    // Clear transition state after animation completes
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isOpen, panelWidth, trackPanelToggle]);

  const openPanel = useCallback(() => {
    if (!isOpen) {
      setIsTransitioning(true);
      setIsOpen(true);

      // Track panel open analytics
      try {
        trackPanelToggle("opened", "auto", panelWidth);
      } catch (error) {
        console.error("Failed to track panel open:", error);
      }

      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [isOpen, panelWidth, trackPanelToggle]);

  const closePanel = useCallback(() => {
    if (isOpen) {
      setIsTransitioning(true);
      setIsOpen(false);

      // Track panel close analytics
      try {
        trackPanelToggle("closed", "auto", panelWidth);
      } catch (error) {
        console.error("Failed to track panel close:", error);
      }

      setTimeout(() => setIsTransitioning(false), 300);
    }
  }, [isOpen, panelWidth, trackPanelToggle]);

  const setHeaderHeight = useCallback((height: number) => {
    setHeaderHeightState(height);
  }, []);

  const rememberTab = useCallback(
    (tab: string, options?: { persist?: boolean }) => {
      setRememberedTab(tab);
      if (options?.persist) {
        localStorageManager.setItem(
          LOCAL_STORAGE_BASE_KEYS.NOTIFICATION_SHEET_TAB,
          tab
        );
      }
    },
    []
  );

  const rememberCardId = useCallback((cardId: string | null) => {
    setRememberedCardId(cardId);
  }, []);

  const setHasSearchResults = useCallback((value: boolean) => {
    setHasSearchResultsState(value);
  }, []);

  const contextValue: InboxPanelState = useMemo(
    () => ({
      isOpen,
      isTransitioning,
      panelWidth,
      headerHeight,
      rememberedTab,
      rememberedCardId,
      hasSearchResults,
      togglePanel,
      openPanel,
      closePanel,
      setHeaderHeight,
      rememberTab,
      rememberCardId,
      setHasSearchResults,
    }),
    [
      isOpen,
      isTransitioning,
      panelWidth,
      headerHeight,
      rememberedTab,
      rememberedCardId,
      hasSearchResults,
      togglePanel,
      openPanel,
      closePanel,
      setHeaderHeight,
      rememberTab,
      rememberCardId,
      setHasSearchResults,
    ]
  );

  return (
    <InboxPanelContext.Provider value={contextValue}>
      {children}
    </InboxPanelContext.Provider>
  );
};
